import { useState, useEffect, useRef, useCallback } from "react";
import { useDirectMessages, useAllProfiles } from "@/hooks/useDirectMessages";
import { useMessageChannels, Channel } from "@/hooks/useMessageChannels";
import { DirectMessageThread } from "@/components/DirectMessageThread";
import { ChannelMessageThread } from "@/components/ChannelMessageThread";
import { CreateChannelDialog } from "@/components/messaging/CreateChannelDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  MessageSquare, Search, Plus, Loader2, X, Hash, Users,
  ChevronUp, ChevronDown, GripHorizontal, Minimize2, Maximize2,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

type ViewMode =
  | { type: "list" }
  | { type: "dm"; partnerId: string; partnerName: string }
  | { type: "channel"; channel: Channel }
  | { type: "newDm" };

interface FloatingChatProps {
  externalOpen?: boolean;
  onExternalOpenChange?: (open: boolean) => void;
  initialChannelId?: string | null;
  initialDmPartnerId?: string | null;
  initialDmPartnerName?: string | null;
}

const STORAGE_KEY_DESKTOP = "floating-chat-pos";
const STORAGE_KEY_MOBILE = "floating-chat-pos-mobile";
const getStorageKey = () => (typeof window !== "undefined" && window.innerWidth < 1024 ? STORAGE_KEY_MOBILE : STORAGE_KEY_DESKTOP);
const HEIGHT_STORAGE_KEY = "floating-chat-height";
const DEFAULT_POS = { x: -1, y: -1 }; // -1 means use default bottom-right
const DEFAULT_HEIGHT = 540;
const MIN_HEIGHT = 400;
const MAX_HEIGHT_VH = 0.85;

export const FloatingChat = ({
  externalOpen,
  onExternalOpenChange,
  initialChannelId,
  initialDmPartnerId,
  initialDmPartnerName,
}: FloatingChatProps) => {
  const [expanded, setExpanded] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>({ type: "list" });
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState<"dms" | "channels">("dms");
  const [hasNewMessage, setHasNewMessage] = useState(false);

  // Drag state
  const [position, setPosition] = useState(DEFAULT_POS);
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  // Resize state
  const [panelHeight, setPanelHeight] = useState(DEFAULT_HEIGHT);
  const [isResizing, setIsResizing] = useState(false);
  const resizeStart = useRef({ y: 0, height: 0, topY: 0 });

  const { conversations, isLoadingConversations, totalUnread: dmUnread } = useDirectMessages();
  const { channels, isLoadingChannels, totalUnread: channelUnread } = useMessageChannels();
  const { data: allProfiles = [], isLoading: isLoadingProfiles } = useAllProfiles();

  const totalUnread = dmUnread + channelUnread;

  const [, forceRender] = useState(0);
  useEffect(() => {
    const on = () => forceRender((n) => n + 1);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);

  // Load saved position and height
  useEffect(() => {
    try {
      const saved = localStorage.getItem(getStorageKey());
      if (saved) setPosition(JSON.parse(saved));
      const savedHeight = localStorage.getItem(HEIGHT_STORAGE_KEY);
      if (savedHeight) setPanelHeight(Number(savedHeight));
    } catch {}
  }, []);

  // Handle external open (from notifications)
  useEffect(() => {
    if (externalOpen) {
      setExpanded(true);
    }
  }, [externalOpen]);

  // Handle initial channel navigation
  useEffect(() => {
    if (expanded && initialChannelId && channels.length > 0) {
      const target = channels.find((c) => c.id === initialChannelId);
      if (target) {
        setActiveTab("channels");
        setViewMode({ type: "channel", channel: target });
      }
    }
  }, [expanded, initialChannelId, channels]);

  // Handle initial DM navigation
  useEffect(() => {
    if (expanded && initialDmPartnerId) {
      setActiveTab("dms");
      setViewMode({ type: "dm", partnerId: initialDmPartnerId, partnerName: initialDmPartnerName || "Unknown" });
    }
  }, [expanded, initialDmPartnerId, initialDmPartnerName]);

  // Keyboard shortcut Ctrl+M / Cmd+M
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "m") {
        e.preventDefault();
        toggleExpanded();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [expanded]);

  // Realtime subscription for pulse animation
  useEffect(() => {
    const channel = supabase
      .channel("floating-chat-realtime")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "direct_messages" }, () => {
        if (!expanded) setHasNewMessage(true);
      })
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "channel_messages" }, () => {
        if (!expanded) setHasNewMessage(true);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [expanded]);

  // Clear pulse when expanded
  useEffect(() => {
    if (expanded) setHasNewMessage(false);
  }, [expanded]);

  const COLLAPSED_BAR_HEIGHT = 56;

  const toggleExpanded = () => {
    const next = !expanded;
    const isCustomPos = position.x !== -1 || position.y !== -1;

    if (isCustomPos) {
      if (!next) {
        // Collapsing: shift top down so the bar appears at the bottom of the panel
        setPosition(prev => ({
          ...prev,
          y: prev.y + panelHeight - COLLAPSED_BAR_HEIGHT,
        }));
      } else {
        // Expanding: shift top back up so the panel grows downward from the bar
        setPosition(prev => ({
          ...prev,
          y: prev.y - panelHeight + COLLAPSED_BAR_HEIGHT,
        }));
      }
    }

    setExpanded(next);
    if (!next) {
      onExternalOpenChange?.(false);
    }
  };

  // Drag handlers
  const resetPosition = useCallback(() => {
    setPosition(DEFAULT_POS);
    try { localStorage.removeItem(getStorageKey()); } catch {}
  }, []);

  const handleDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!containerRef.current) return;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const rect = containerRef.current.getBoundingClientRect();
    dragOffset.current = { x: clientX - rect.left, y: clientY - rect.top };
    setIsDragging(true);
  }, []);

  useEffect(() => {
    if (!isDragging) return;
    const handleMove = (e: MouseEvent | TouchEvent) => {
      const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY;
      if ('touches' in e) e.preventDefault();
      const x = clientX - dragOffset.current.x;
      const y = clientY - dragOffset.current.y;
      setPosition({ x, y });
    };
    const handleUp = () => {
      setIsDragging(false);
      setPosition((pos) => {
        localStorage.setItem(getStorageKey(), JSON.stringify(pos));
        return pos;
      });
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    window.addEventListener("touchmove", handleMove, { passive: false });
    window.addEventListener("touchend", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleUp);
    };
  }, [isDragging]);

  // Resize handlers
  const handleResizeStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const currentTop = containerRef.current ? containerRef.current.getBoundingClientRect().top : position.y;
    resizeStart.current = { y: clientY, height: panelHeight, topY: currentTop };
    setIsResizing(true);
  }, [panelHeight, position.y]);

  useEffect(() => {
    if (!isResizing) return;
    const handleMove = (e: MouseEvent | TouchEvent) => {
      const clientY = 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY;
      if ('touches' in e) e.preventDefault();
      const maxHeight = window.innerHeight * MAX_HEIGHT_VH;
      const delta = resizeStart.current.y - clientY;
      const newHeight = Math.min(maxHeight, Math.max(MIN_HEIGHT, resizeStart.current.height + delta));
      setPanelHeight(newHeight);

      if (position.x !== -1 || position.y !== -1) {
        const newTop = resizeStart.current.topY + (resizeStart.current.height - newHeight);
        setPosition(prev => ({ ...prev, y: newTop }));
      }
    };
    const handleUp = () => {
      setIsResizing(false);
      setPanelHeight((h) => {
        localStorage.setItem(HEIGHT_STORAGE_KEY, String(h));
        return h;
      });
      setPosition((pos) => {
        if (pos.x !== -1 || pos.y !== -1) {
          localStorage.setItem(getStorageKey(), JSON.stringify(pos));
        }
        return pos;
      });
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    window.addEventListener("touchmove", handleMove, { passive: false });
    window.addEventListener("touchend", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleUp);
    };
  }, [isResizing]);

  const getInitials = (name: string) =>
    name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

  const filteredProfiles = allProfiles.filter((p) => {
    const q = searchTerm.toLowerCase();
    return (
      (p.full_name || "").toLowerCase().includes(q) ||
      (p.email || "").toLowerCase().includes(q)
    );
  });

  const startNewChat = (profile: { id: string; full_name: string | null; email: string | null }) => {
    setViewMode({
      type: "dm",
      partnerId: profile.id,
      partnerName: profile.full_name || profile.email || "Unknown",
    });
    setSearchTerm("");
  };

  const handleBack = () => setViewMode({ type: "list" });

  // Last message preview for collapsed bar
  const lastConvo = conversations[0];
  const lastChannelMsg = channels.find((c) => c.lastMessage);
  const previewText =
    lastConvo?.lastMessage || lastChannelMsg?.lastMessage || "No messages yet";

  // Position styles
  const isDefaultPos = position.x === -1 && position.y === -1;
  const vw = typeof window !== "undefined" ? window.innerWidth : 1280;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const isSmall = vw < 1024;
  const panelWidth = isSmall ? (expanded ? vw - 16 : Math.min(300, vw - 16)) : 380;
  const boxH = expanded ? (isSmall ? Math.min(panelHeight, vh - 100) : panelHeight) : 56;
  const clampX = (x: number) => Math.max(8, Math.min(x, vw - panelWidth - 8));
  const clampY = (y: number) => Math.max(8, Math.min(y, vh - boxH - 8));
  const posStyle: React.CSSProperties = isDefaultPos
    ? isSmall ? { bottom: 76, right: 8 } : { bottom: 24, right: 24 }
    : { top: clampY(position.y), left: clampX(position.x) };

  return (
    <div
      ref={containerRef}
      className={cn(
        "fixed z-50 flex flex-col",
        (isDragging || isResizing) && "select-none"
      )}
      style={{
        ...posStyle,
        width: panelWidth,
        ...(expanded ? { height: isSmall ? Math.min(panelHeight, vh - 100) : panelHeight } : {}),
      }}
    >
      {/* Expanded panel */}
      {expanded && (
        <div className="flex-1 bg-card border border-border rounded-t-xl shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 duration-200">
          {/* Resize handle at top */}
          <div
            className="h-1.5 cursor-ns-resize flex items-center justify-center hover:bg-muted/60 transition-colors group shrink-0"
            onMouseDown={handleResizeStart}
            onTouchStart={handleResizeStart}
          >
            <div className="w-10 h-0.5 rounded-full bg-border group-hover:bg-muted-foreground/40 transition-colors" />
          </div>
          <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/50 shrink-0">
            <div
              className="flex items-center gap-2 cursor-grab active:cursor-grabbing flex-1"
              onMouseDown={handleDragStart}
              onTouchStart={handleDragStart}
              onDoubleClick={resetPosition}
              style={{ touchAction: "none" }}
            >
              <GripHorizontal className="h-4 w-4 text-muted-foreground" />
              <MessageSquare className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-foreground">Messages</span>
              {totalUnread > 0 && (
                <Badge variant="destructive" className="h-5 min-w-5 flex items-center justify-center p-0 text-xs">
                  {totalUnread > 9 ? "9+" : totalUnread}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1">
              {viewMode.type === "list" && (
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setViewMode({ type: "newDm" })}>
                  <Plus className="h-4 w-4" />
                </Button>
              )}
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={toggleExpanded}>
                <Minimize2 className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {viewMode.type === "dm" ? (
              <DirectMessageThread
                partnerId={viewMode.partnerId}
                partnerName={viewMode.partnerName}
                onBack={handleBack}
              />
            ) : viewMode.type === "channel" ? (
              <ChannelMessageThread
                channel={viewMode.channel}
                onBack={handleBack}
              />
            ) : viewMode.type === "newDm" ? (
              <>
                <div className="flex items-center justify-between px-3 py-2 border-b">
                  <span className="text-sm font-medium">New Message</span>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleBack}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="p-2 border-b">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      placeholder="Search people..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-8 h-8 text-sm"
                    />
                  </div>
                </div>
                <ScrollArea className="flex-1">
                  {isLoadingProfiles ? (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : filteredProfiles.length === 0 ? (
                    <p className="text-center text-sm text-muted-foreground py-6">No users found</p>
                  ) : (
                    <div className="divide-y">
                      {filteredProfiles.map((p) => (
                        <button
                          key={p.id}
                          className="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-accent text-left"
                          onClick={() => startNewChat(p)}
                        >
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="bg-primary/10 text-primary text-xs">
                              {getInitials(p.full_name || p.email || "?")}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{p.full_name || "Unknown"}</p>
                            <p className="text-xs text-muted-foreground truncate">{p.email}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </>
            ) : (
              /* List view with tabs */
              <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="flex flex-col flex-1 overflow-hidden">
                <TabsList className="grid w-full grid-cols-2 mx-2 mt-2 mb-0 w-auto">
                  <TabsTrigger value="dms" className="text-xs relative">
                    <Users className="h-3 w-3 mr-1" />
                    DMs
                    {dmUnread > 0 && (
                      <Badge variant="destructive" className="absolute -top-1 -right-1 h-4 w-4 p-0 text-[10px] flex items-center justify-center">
                        {dmUnread > 9 ? "9+" : dmUnread}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="channels" className="text-xs relative">
                    <Hash className="h-3 w-3 mr-1" />
                    Channels
                    {channelUnread > 0 && (
                      <Badge variant="destructive" className="absolute -top-1 -right-1 h-4 w-4 p-0 text-[10px] flex items-center justify-center">
                        {channelUnread > 9 ? "9+" : channelUnread}
                      </Badge>
                    )}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="dms" className="flex-1 mt-0 overflow-hidden data-[state=active]:flex flex-col">
                  <ScrollArea className="flex-1 min-h-0">
                    {isLoadingConversations ? (
                      <div className="flex items-center justify-center py-6">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : conversations.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-8 text-center px-4">
                        <Users className="h-8 w-8 text-muted-foreground/50 mb-2" />
                        <p className="text-muted-foreground text-sm">No conversations yet</p>
                        <Button size="sm" className="mt-3" onClick={() => setViewMode({ type: "newDm" })}>
                          <Plus className="h-4 w-4 mr-1" />
                          New Message
                        </Button>
                      </div>
                    ) : (
                      <div className="divide-y">
                        {conversations.map((c) => (
                          <button
                            key={c.partnerId}
                            className={cn(
                              "w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-accent text-left",
                              c.unreadCount > 0 && "bg-accent/50"
                            )}
                            onClick={() =>
                              setViewMode({ type: "dm", partnerId: c.partnerId, partnerName: c.partnerName })
                            }
                          >
                            <Avatar className="h-8 w-8">
                              <AvatarFallback className="bg-primary/10 text-primary text-xs">
                                {getInitials(c.partnerName)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-1">
                                <p className={cn("text-sm truncate", c.unreadCount > 0 && "font-semibold")}>
                                  {c.partnerName}
                                </p>
                                <span className="text-[10px] text-muted-foreground shrink-0">
                                  {formatDistanceToNow(new Date(c.lastMessageAt), { addSuffix: true })}
                                </span>
                              </div>
                              <div className="flex items-center justify-between gap-1">
                                <p className={cn(
                                  "text-xs truncate",
                                  c.unreadCount > 0 ? "text-foreground font-medium" : "text-muted-foreground"
                                )}>
                                  {c.lastMessage}
                                </p>
                                {c.unreadCount > 0 && (
                                  <Badge variant="default" className="h-4 min-w-4 flex items-center justify-center p-0 text-[10px]">
                                    {c.unreadCount}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </TabsContent>

                <TabsContent value="channels" className="flex-1 mt-0 overflow-hidden data-[state=active]:flex flex-col">
                  <div className="p-2 border-b shrink-0">
                    <CreateChannelDialog
                      trigger={
                        <Button size="sm" variant="outline" className="w-full h-7 text-xs">
                          <Plus className="h-3.5 w-3.5 mr-1" />
                          Create Channel
                        </Button>
                      }
                    />
                  </div>
                  <ScrollArea className="flex-1 min-h-0">
                    {isLoadingChannels ? (
                      <div className="flex items-center justify-center py-6">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : channels.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-8 text-center px-4">
                        <Hash className="h-8 w-8 text-muted-foreground/50 mb-2" />
                        <p className="text-muted-foreground text-sm">No channels</p>
                        <CreateChannelDialog />
                      </div>
                    ) : (
                      <div className="divide-y">
                        {channels.map((ch) => (
                          <button
                            key={ch.id}
                            className={cn(
                              "w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-accent text-left",
                              (ch.unreadCount || 0) > 0 && "bg-accent/50"
                            )}
                            onClick={() => setViewMode({ type: "channel", channel: ch })}
                          >
                            <div
                              className="h-8 w-8 rounded flex items-center justify-center shrink-0"
                              style={{ backgroundColor: ch.department?.color || "hsl(var(--primary))" }}
                            >
                              <Hash className="h-4 w-4 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-1">
                                <p className={cn("text-sm min-w-0 break-words [overflow-wrap:anywhere]", (ch.unreadCount || 0) > 0 && "font-semibold")}>
                                  {ch.name}
                                </p>
                                {ch.lastMessageAt && (
                                  <span className="text-[10px] text-muted-foreground shrink-0">
                                    {formatDistanceToNow(new Date(ch.lastMessageAt), { addSuffix: true })}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-start justify-between gap-1">
                                <p className={cn(
                                  "text-xs min-w-0 break-words [overflow-wrap:anywhere] whitespace-pre-wrap",
                                  (ch.unreadCount || 0) > 0 ? "text-foreground font-medium" : "text-muted-foreground"
                                )}>
                                  {ch.lastMessage || ch.department?.name || "No messages yet"}
                                </p>
                                {(ch.unreadCount || 0) > 0 && (
                                  <Badge variant="default" className="h-4 min-w-4 shrink-0 flex items-center justify-center p-0 text-[10px]">
                                    {ch.unreadCount}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </TabsContent>
              </Tabs>
            )}
          </div>
        </div>
      )}

      {/* Collapsed bar — draggable via grip, click rest to toggle */}
      <div
        className={cn(
          "flex items-center bg-primary text-primary-foreground shadow-lg transition-all",
          expanded ? "rounded-b-xl" : "rounded-xl",
          hasNewMessage && !expanded && "animate-pulse"
        )}
      >
        {/* Drag grip area */}
        <div
          className="flex items-center justify-center px-2 py-3 cursor-grab active:cursor-grabbing shrink-0"
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          onDoubleClick={resetPosition}
          style={{ touchAction: "none" }}
          onClick={(e) => e.stopPropagation()}
        >
          <GripHorizontal className="h-4 w-4 text-primary-foreground/60" />
        </div>
        {/* Clickable area */}
        <button
          className="flex items-center gap-3 flex-1 min-w-0 pr-4 py-3"
          onClick={toggleExpanded}
        >
          <MessageSquare className="h-5 w-5 shrink-0" />
          <div className="flex-1 min-w-0 text-left">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-semibold">Messages</span>
              {totalUnread > 0 && (
                <Badge className="bg-background text-foreground h-5 min-w-5 flex items-center justify-center p-0 text-xs">
                  {totalUnread > 9 ? "9+" : totalUnread}
                </Badge>
              )}
            </div>
            {!expanded && (
              <p className="text-xs text-primary-foreground/70 truncate mt-0.5">
                {previewText}
              </p>
            )}
          </div>
          {expanded ? (
            <ChevronDown className="h-4 w-4 shrink-0" />
          ) : (
            <ChevronUp className="h-4 w-4 shrink-0" />
          )}
        </button>
      </div>
    </div>
  );
};
